import { useNavigate } from 'react-router';
import { EntityDisplayName } from '@backstage/plugin-catalog-react';
import { parseEntityRef } from '@backstage/catalog-model';
import { Box, Text } from '@backstage/ui';
import { RiExternalLinkLine } from '@remixicon/react';
import { DotIcon } from '@xlrt/components';
import qs from 'qs';

import { typeIconMap } from '../../utils/typeIcons';
import { getDisplayType, SEARCH_TYPES } from '../../utils/search.constant';
import styles from './SearchResultItems.module.css';

type SearchResultItemsProps = {
  result: {
    title: string;
    location: string;
    owner?: string;
    kind?: string;
    namespace?: string;
    spaceName?: string;
    createdBy?: string;
  };
  type: string;
  rank?: number;
  onClick: () => void;
};

export const SearchResultItems = ({
  result,
  type,
  onClick,
}: SearchResultItemsProps) => {
  const { title, kind, location, owner, spaceName } = result;
  const iconKey = type === SEARCH_TYPES.CATALOG ? kind || 'Component' : type;
  const Icon = typeIconMap[iconKey];
  const navigate = useNavigate();
  const [basePath, queryString = ''] = location.split('?');
  const queryObj = qs.parse(queryString);
  const mergedQuery = qs.stringify({ ...queryObj, ref: 'search' });
  const updatedLocation = `${basePath}?${mergedQuery}`;

  const handleClick = () => {
    onClick();
    if (type === SEARCH_TYPES.CONFLUENCE) {
      window.open(updatedLocation, '_blank');
    } else {
      navigate(updatedLocation);
    }
  };

  return (
    <Box
      className={styles.item}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <Box className={styles.icon}>{Icon ? <Icon /> : null}</Box>
      <Box className={styles.content}>
        <Text as="span" className={styles.title}>
          {title}
        </Text>
        <Box className={styles.description}>
          {(type !== SEARCH_TYPES.CATALOG || kind) && (
            <Text as="span" className={styles.descriptionText}>
              {getDisplayType(type, kind)}
            </Text>
          )}
          {type === SEARCH_TYPES.CONFLUENCE && spaceName && (
            <>
              <DotIcon />
              <Text as="span" className={styles.descriptionText}>
                {spaceName}
              </Text>
            </>
          )}
          {owner && (
            <>
              <DotIcon />
              <Text as="span" className={styles.descriptionText}>
                <EntityDisplayName
                  entityRef={parseEntityRef(owner, {
                    defaultKind: 'Group',
                    defaultNamespace: 'default',
                  })}
                  hideIcon
                  disableTooltip
                />
              </Text>
            </>
          )}
        </Box>
      </Box>
      {type === SEARCH_TYPES.CONFLUENCE && (
        <Box className={styles.action}>
          <RiExternalLinkLine />
        </Box>
      )}
    </Box>
  );
};
